class FinanceModule extends HTMLElement {
  connectedCallback () {
    this.financeModuleContent = this.querySelector('.finance-module__content')
    this.financeLinkText = this.querySelector('.price__finance-link-text')

    const configScript = this.querySelector('.finance-module__config')
    if (configScript) {
      try {
        this.config = JSON.parse(configScript.textContent)
      } catch (e) {
        this.config = null
      }
    }

    if (this.financeModuleContent && this.financeLinkText) {
      this.loading = true
      this.updateFinanceFromText(this.financeLinkText)

      const displayType = this.dataset.financeDisplayType || 'modal'

      if (displayType === 'link') {
        this.linkClickHandler = (event) => {
          event.preventDefault()
          const linkUrl = this.dataset.financeLinkUrl
          if (linkUrl) {
            window.open(linkUrl, '_blank')
          }
        }
        this.addEventListener('click', this.linkClickHandler)
      } else {
        this.modalClickHandler = (event) => {
          event.preventDefault()
          this.openModal()
        }
        this.addEventListener('click', this.modalClickHandler)
      }
    }

    this.onVariantChange = this.onVariantChange.bind(this)
    document.addEventListener('variantChange', this.onVariantChange)
  }

  disconnectedCallback () {
    document.removeEventListener('variantChange', this.onVariantChange)
  }

  onVariantChange () {
    this.loading = true
  }

  updateFinanceFromText (financeLinkText) {
    const priceInCents = parseFloat(this.price)
    const financeTerms = window.Tentbox.financeTerms || window.FinanceUtils.parseFinanceTerms(this.dataset.financeTerms)

    if (!financeTerms.length) {
      this.loading = false
      return
    }

    const monthlyPayment = window.FinanceUtils.calculateMinMonthlyPayment(priceInCents, financeTerms)
    const rounded = Math.ceil(monthlyPayment)
    const formatted = this.formatPrice(rounded)

    let financeFromTranslation = this.dataset.financeFromTranslation || ''
    financeFromTranslation = financeFromTranslation.replace(/\[\[PRICE\]\]/g, formatted)
    financeFromTranslation = financeFromTranslation.replace(/\{\{\s*price\s*\}\}/g, formatted)
    if (financeLinkText) {
      financeLinkText.textContent = financeFromTranslation
    }

    setTimeout(() => {
      this.loading = false
    }, 250)
  }

  openModal () {
    window.dispatchEvent(
      new CustomEvent('open:modal', {
        detail: {
          width: 'min(600px, 100svw)',
          modalClassAdd: ['py-4', 'max-h-[90vh]'],
          modalClassRemove: ['p-4', 'md:p-4', 'max-h-[80vh]'],
          headerClassAdd: ['px-4'],
          headerClassRemove: ['mb-4'],
          bodyClassAdd: ['px-4', 'pt-4', 'pb-6'],
          footerClassAdd: ['px-4'],
          faderClassAdd: ['left-0', 'right-0'],
          faderClassRemove: ['left-4', 'right-4', 'md:left-4', 'md:right-4'],
        },
      }),
    )

    const checkModalReady = () => {
      this.modalHeaderTitle = this.modal.querySelector('.reusable-modal__title')
      this.modalBody = this.modal.querySelector('.reusable-modal__body')

      if (!this.modalHeaderTitle || !this.modalBody) {
        requestAnimationFrame(checkModalReady)
        return
      }

      this.setModalContent()
    }

    checkModalReady()
  }

  setModalContent () {
    const priceInCents = parseFloat(this.price)
    if (isNaN(priceInCents)) return
    const price = priceInCents / 100

    if (!this.config || !this.config.providers || !this.config.providers.length) return

    const { modalTitle, tabInstruction, providers } = this.config
    const hasMultiple = providers.length > 1

    let html = ''

    if (hasMultiple && tabInstruction) {
      html += `<p class="finance-modal__tab-instruction font-medium text-lg leading-snug text-black">${this.escapeHtml(tabInstruction)}</p>`
    }

    if (hasMultiple) {
      html += `<div class="finance-modal__tabs flex items-stretch gap-0" role="tablist">`
      providers.forEach((provider, i) => {
        if (i > 0) {
          html += `<div class="finance-modal__tab-separator flex flex-col items-center px-3"><span class="flex-1 w-px bg-clay"></span><span class="text-sm text-iron font-medium leading-none py-1">or</span><span class="flex-1 w-px bg-clay"></span></div>`
        }
        const isActive = i === 0
        const borderClass = isActive ? 'border-black' : 'border-clay'
        const logoHtml = this.renderProviderLogo(provider)
        const tagHtml = provider.tag
          ? `<span class="finance-modal__tab-tag mt-1.5 text-center leading-relaxed"><span class="inline box-decoration-clone text-xs font-semibold rounded-md px-2.5 py-0.5" style="${this.getTagStyle(provider.tagColor)}">${this.escapeHtml(provider.tag)}</span></span>`
          : ''
        const tickSvg = this.config?.tickIconSvg || ''
        const tickHtml = `<span class="finance-modal__tab-tick absolute top-1.5 right-1.5 flex items-center justify-center w-6 h-6 rounded-full bg-success text-white p-1 ${isActive ? '' : 'hidden'}">${tickSvg}</span>`
        html += `
          <button
            class="finance-modal__tab relative ${borderClass} border rounded-lg flex-1 flex flex-col items-center justify-center gap-1.5 pt-3 pb-2.5 px-3 cursor-pointer bg-white transition-colors"
            role="tab"
            aria-selected="${isActive}"
            data-provider-index="${i}"
          >${tickHtml}${logoHtml}${tagHtml}</button>`
      })
      html += `</div>`
    } else {
      const logoHtml = this.renderProviderLogo(providers[0])
      if (logoHtml) {
        html += `<div class="finance-modal__single-logo flex items-center py-2">${logoHtml}</div>`
      }
    }

    providers.forEach((provider, i) => {
      if (!provider.tabText) return
      const hidden = i > 0 ? ' hidden' : ''
      html += `<div class="finance-modal__tab-text text-base text-black whitespace-pre-line${hidden}" data-provider-tab-text="${i}" role="region">${this.escapeHtml(provider.tabText)}</div>`
    })

    providers.forEach((provider, i) => {
      const hidden = i > 0 ? ' hidden' : ''
      html += `<div class="finance-modal__panel${hidden}" data-provider-panel="${i}" role="tabpanel">`
      html += this.generateProviderContent(provider, price)
      html += `</div>`
    })

    if (this.config.legalNoticeHtml) {
      html += `<div class="finance-modal__shared-legal-notice pb-2">${this.decodeHtml(this.config.legalNoticeHtml)}</div>`
    }

    this.modalHeaderTitle.textContent = modalTitle || ''
    this.modalBody.innerHTML = `<div class="finance-modal flex flex-col gap-4">${html}</div>`

    if (hasMultiple) {
      this.modalBody.querySelectorAll('.finance-modal__tab').forEach(tab => {
        tab.addEventListener('click', () => {
          this.switchProvider(parseInt(tab.dataset.providerIndex, 10))
        })
      })
    }
  }

  generateProviderContent (provider, price) {
    const terms = provider.terms || []
    const t = this.config?.translations || {}
    if (!terms.length) return `<p class="text-sm text-iron">${this.escapeHtml(t.noOptions || '')}</p>`

    let html = '<div class="finance-modal__cards flex flex-col gap-3">'
    terms.forEach(term => {
      html += this.generateFinanceCard(term, price)
    })
    html += '</div>'

    if (provider.howToApply) {
      const howToApplyTitle = this.config?.translations?.howToApplyTitle || 'How to apply:'
      html += `
        <div class="finance-modal__how-to-apply flex flex-col gap-2.5 py-5 mt-2.5 border-b border-clay">
          <div class="finance-modal__how-to-apply-title font-medium text-lg leading-snug text-black tb-in-page-content [&>p]:mb-0">${this.decodeHtml(howToApplyTitle)}</div>
          <div class="tb-in-page-content tb-list-fix text-base text-black">${this.decodeHtml(provider.howToApply)}</div>
        </div>
        <div class="finance-modal__divider bg-clay/50 h-px w-full"></div>`
    }

    if (provider.legal) {
      if (!provider.howToApply) {
        html += `<div class="finance-modal__divider bg-clay/50 h-px w-full"></div>`
      }
      html += `<div class="finance-modal__legal text-sm text-black tb-in-page-content pt-5 tb-list-fix">${this.decodeHtml(provider.legal)}</div>`
    }

    return html
  }

  generateFinanceCard (term, price) {
    const FU = window.FinanceUtils
    const rateType = term.rateType || 'apr'
    const monthlyPayment = Math.ceil(FU.calculateMonthlyPayment(price, term.rate, term.period, rateType))
    const totalPayable = Math.ceil(FU.calculateTotalPayable(price, term.rate, term.period, rateType))

    const formattedMonthly = this.formatPrice(monthlyPayment)
    const formattedTotal = this.formatPrice(totalPayable)
    const t = this.config?.translations || {}
    const rateNum = Number(term.rate)
    const isZero = rateNum === 0 && !Number.isNaN(rateNum)
    const rateTemplate = rateType === 'fee'
      ? (t.fee || '{{ rate }}% fee')
      : (t.interest || '{{ rate }}% APR')
    const interestLabel = isZero
      ? (t.interestZero || '0% APR')
      : rateTemplate.replace('{{ rate }}', term.rate)
    const totalLabelRaw = t.totalPayable || 'Total payable:'
    const perMonthSuffix = t.perMonthSuffix || '/month'

    const totalHtml = totalLabelRaw.includes('{{ amount }}')
      ? `<span>${this.escapeHtml(totalLabelRaw).replace('{{ amount }}', formattedTotal)}</span>`
      : `<span>${this.escapeHtml(totalLabelRaw)}</span><span>${formattedTotal}</span>`

    return `
      <div class="finance-card bg-oat/40 border border-clay rounded-lg px-3.5 py-3 flex flex-col">
        <div class="finance-card__header flex items-center justify-between gap-3 pb-2.5 mb-2.5 border-b border-clay/60">
          <span class="finance-card__title font-semibold text-lg leading-snug text-black">${term.period} × ${formattedMonthly}${this.escapeHtml(perMonthSuffix)}</span>
          <span class="finance-card__badge whitespace-nowrap border border-orange text-orange font-semibold text-[15px] leading-none rounded-[3px] px-1.5 py-1">${this.escapeHtml(interestLabel)}</span>
        </div>
        <div class="finance-card__total flex flex-wrap gap-x-1.5 text-sm font-medium text-black">
          ${totalHtml}
        </div>
      </div>`
  }

  switchProvider (index) {
    const modal = this.modalBody || this.modal.querySelector('.reusable-modal__body')
    if (!modal) return

    modal.querySelectorAll('.finance-modal__tab').forEach(tab => {
      const isActive = parseInt(tab.dataset.providerIndex, 10) === index
      tab.setAttribute('aria-selected', isActive)
      const tick = tab.querySelector('.finance-modal__tab-tick')
      if (isActive) {
        tab.classList.remove('border-clay')
        tab.classList.add('border-black')
        if (tick) tick.classList.remove('hidden')
      } else {
        tab.classList.remove('border-black')
        tab.classList.add('border-clay')
        if (tick) tick.classList.add('hidden')
      }
    })

    modal.querySelectorAll('.finance-modal__panel').forEach(panel => {
      const panelIndex = parseInt(panel.dataset.providerPanel, 10)
      if (panelIndex === index) {
        panel.classList.remove('hidden')
      } else {
        panel.classList.add('hidden')
      }
    })

    modal.querySelectorAll('.finance-modal__tab-text').forEach(el => {
      const textIndex = parseInt(el.dataset.providerTabText, 10)
      if (textIndex === index) {
        el.classList.remove('hidden')
      } else {
        el.classList.add('hidden')
      }
    })
  }

  getTagStyle (color) {
    const map = {
      green: 'background: #dcfce7; color: #166534;',
      orange: 'background: #fff1e6; color: #c2410c;',
      blue: 'background: #2563EB1A; color: #2563EB;',
      neutral: 'background: #f3f4f6; color: #4b5563;',
    }
    return map[color] || map.neutral
  }

  renderProviderLogo (provider) {
    if (provider.logoSvg) {
      const decoded = this.decodeHtml(provider.logoSvg)
      return `<span class="finance-modal__logo-svg inline-flex items-center justify-center shrink-0" style="height: 32px; max-width: 100%;"><span class="h-full flex items-center justify-center [&>svg]:h-full [&>svg]:w-auto">${decoded}</span></span>`
    } else if (provider.logo) {
      return `<img class="finance-modal__logo-img" src="${this.escapeHtml(provider.logo)}" alt="" style="height: 32px; width: auto; max-width: 100%;" />`
    }
    return ''
  }

  formatPrice (amount) {
    if (window.FinanceUtils?.formatMoneyAmount) {
      return window.FinanceUtils.formatMoneyAmount(amount)
    }
    const currencySymbol = this.getCurrencySymbol()
    return `${currencySymbol}${Math.round(amount)}`
  }

  getCurrencySymbol () {
    const currencyCode = window.Shopify.currency.active
    switch (currencyCode) {
      case 'USD':
        return '$'
      case 'EUR':
        return '€'
      case 'GBP':
        return '£'
      default:
        return ''
    }
  }

  escapeHtml (str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.appendChild(document.createTextNode(str))
    return div.innerHTML
  }

  decodeHtml (html) {
    if (!html) return ''
    const txt = document.createElement('textarea')
    txt.innerHTML = html
    return txt.value
  }

  get modal () {
    return document.querySelector('.reusable-modal')
  }

  get price () {
    return this.dataset.productPrice
  }

  set loading (val) {
    const productPrice = this.closest('.product-price')
    if (productPrice) {
      if (val) {
        productPrice.setAttribute('loading', 'true')
        productPrice.setAttribute('aria-busy', 'true')
      } else {
        productPrice.removeAttribute('loading')
        productPrice.removeAttribute('aria-busy')
      }
    }
  }

  get loading () {
    const productPrice = this.closest('.product-price')
    if (productPrice) {
      return productPrice.getAttribute('loading') === 'true'
    }
    return false
  }
}

if (!customElements.get('finance-module')) {
  customElements.define('finance-module', FinanceModule)
}
